import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { DeviceDetectService } from '@app/core/services/device-detect/device-detect.service';
import { CustomService } from '@app/core/services/drupal8';
import { EreaderControllerService } from '@app/core/services/ereader-controller/ereader-controller.service';
import { ModalManagerService } from '@app/core/services/modal-manager';
import { NavDataService } from '@app/core/services/nav-data/nav-data.service';
import { RolesService } from '@app/core/services/roles/roles.service';

import { AuthSessionService } from '@app/core/services/auth-session/auth-session.service';
import { CrispChatService } from '@app/core/services/crisp-chat/crisp-chat.service';
import { AnnouncementsService } from '@app/pages/announcements/announcements.service';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
/**
 * SidebarComponent is responsible for rendering and managing the application's sidebar navigation for logged-in users.
 *
 * Features:
 * - Displays navigation options based on user roles (e.g., school, instructor, kiter).
 * - Integrates with Crisp chat and announcements services.
 * - Handles logout and navigation to key pages (login, bookings, courses, profile).
 * - Reacts to language changes and updates unread announcements count.
 * - Manages session and user-specific sidebar content.
 *
 * Usage example (in a parent template):
 * ```
 * <app-sidebar></app-sidebar>
 * ```
 * The sidebar will automatically display navigation options relevant to the current user's roles and session.
 */
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  /**
   * Current environment configuration.
   */
  env = environment;

  /**
   * Announcements service for managing unread count and refresh.
   */
  protected readonly announcementsService = inject(AnnouncementsService);

  /**
   * Signal indicating if the Students section should be shown (based on user role).
   */
  protected readonly canShowStudents = signal(false);

  /**
   * Service for handling translations and language changes.
   */
  private readonly translateService = inject(TranslateService);

  /**
   * Service for managing authentication session and user profile.
   */
  private readonly authSessionService: AuthSessionService = inject(AuthSessionService);

  /**
   * Subject to emit and complete all subscriptions on destroy.
   * Used with takeUntil to prevent memory leaks.
   */
  private readonly unsubscribeAll = new Subject<void>();

  /**
   * Constructs the SidebarComponent and sets up user profile subscription for role-based UI.
   * @param ereaderControllerService Service for e-reader/table of contents state.
   * @param navDataService Service for navigation and sidebar state.
   * @param deviceDetectService Service for device detection.
   * @param rolesService Service for user role management.
   * @param modalManagerService Service for modal management.
   * @param customService Service for custom session/logout logic.
   * @param crispChatService Service for Crisp chat integration.
   */
  constructor(
    public ereaderControllerService: EreaderControllerService,
    public navDataService: NavDataService,
    public deviceDetectService: DeviceDetectService,
    public rolesService: RolesService,
    public modalManagerService: ModalManagerService,
    public customService: CustomService, // private router: Router, // private localizeRouter: LocalizeRouterService
    private crispChatService: CrispChatService
  ) {
    // Subscribe to user profile changes to update the visibility of the Students section.
    this.authSessionService.currentUserProfile$.pipe(takeUntil(this.unsubscribeAll)).subscribe(() => {
      this.canShowStudents.set(
        this.rolesService.isInstructor || (this.rolesService.isSchool && !this.rolesService.isSchoolExpxired)
      );
    });
  }

  /**
   * Cleans up all subscriptions and resources on component destroy.
   * Ensures no memory leaks from observables.
   */
  ngOnDestroy() {
    this.unsubscribeAll.next();
    this.unsubscribeAll.complete();
  }

  /**
   * Initializes chat, announcements refresh, and language change subscription.
   * Sets up listeners for language changes to refresh announcements.
   */
  ngOnInit() {
    this.crispChatService.initChatBot();
    this.announcementsService.startUnreadCountRefresh();
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscribeAll)).subscribe(() => {
      this.announcementsService.startUnreadCountRefresh();
    });
    // TODO: refactory with the new history
    // const lastIndex = this.navDataService.navigationHistory.length - 1;
    // this.navDataService.currentPage = this.navDataService.navigationHistory[lastIndex];
    // if (this.navDataService.currentPage === '/') {
    //   this.navDataService.currentPage = '/account';
    // }
  }

  /**
   * Handles user logout, prevents duplicate logout, and closes the sidebar menu.
   * Calls the customService.logout() method and closes the side menu.
   */
  logout() {
    if (this.customService.logOutInProgress$.value) {
      return;
    }
    this.customService.logout();
    this.navDataService.sideMenuOpen = false;
  }

  /**
   * Navigates to the login page, clears the current user profile, and closes the sidebar menu.
   * Used when the user needs to be redirected to login.
   */
  navigateToLogin() {
    delete this.authSessionService.currentUserProfile;
    // close side menu
    this.navDataService.toggleSideMenu();
    // navigate to login
    this.navDataService.navigate('/user/login');
  }

  /**
   * Navigates to the appropriate bookings page based on the user's role and environment settings.
   * Shows a modal if the school account is expired or suspended.
   */
  navigateToBookings() {
    if (this.rolesService.userHasRole('school', true)) {
      if (this.rolesService.userHasRole(['school_expired', 'school_suspended'], true)) {
        this.modalManagerService.toggle();
      } else {
        if (environment.inAppBrowsing.bookings) {
          this.navDataService.navigate('/account/pro-training', true);
          return;
        }
        this.navDataService.navigate('/account/recreational-training');
      }
    } else if (
      this.rolesService.userHasRole(
        ['kiter', 'instructor', 'assistant_trainer', 'coach', 'examiner', 'administrator', 'suspended'],
        true
      )
    ) {
      // if (environment.inAppBrowsing.bookings) {
      //   this.navDataService.navigate('/account/booking', true);
      //   return;
      // }
      this.navDataService.navigate('/bookings/userBookings');
    } else {
      this.navDataService.navigate('/account');
    }
  }

  /**
   * Navigates to the appropriate courses page based on the user's role and environment settings.
   */
  navigateToCourses() {
    if (this.rolesService.userHasRole(['assistant_trainer', 'examiner'], true)) {
      if (environment.inAppBrowsing.course) {
        this.navDataService.navigate('https://www.ikointl.com/account/pro-training', true);
        return;
      }
      this.navDataService.navigate('/courses/trainer');
      return;
    }
    // school
    if (environment.inAppBrowsing.bookings) {
      this.navDataService.navigate('/account/pro-training', true);
      return;
    }
    this.navDataService.navigate('/account/training');
  }

  /**
   * Opens the Crisp chat widget for user support.
   */
  openCrispChat() {
    this.crispChatService.openChat();
  }

  /**
   * Navigates to the user's profile page, with special handling for school users and production environment.
   */
  navigateToProfile() {
    if (this.rolesService.userHasRole('school', true)) {
      if (environment.production) {
        this.navDataService.navigate(
          `https://www.ikointl.com/user/${this.authSessionService.currentUserProfile?.uid}/edit`,
          true
        );
      } else {
        this.navDataService.navigate('/profile-old/school');
      }
      return;
    }
    this.navDataService.navigate('/profile');
  }
}
